import { For } from "@soft-db/soft-utils";
import { FormField, Select, SelectOption } from "@soft-db/ui";
import { DateTime } from "luxon";
import { PropType } from "vue";
import { useCurrentUser } from "../../api/current-user";

type TimeItem = {
	value: number;
	text: string;
	disabled?: boolean;
};

export default defineComponent({
	name: "AlertSchedulerTimePicker",
	props: {
		modelValue: Number,
		nextDayBetween: Array as PropType<number[]>,
		availableRange: Array as PropType<number[]>,
		label: String,
	},
	emits: ["blur", "update:modelValue"],
	setup(props, { emit }) {
		const { t } = useI18n();
		const max = 24 * 60;
		const step = 15;

		const vModel = useVModel(props, "modelValue");
		const label = useVModel(props, "label");

		const { data: user } = useCurrentUser();

		function isNextDay(value: number) {
			if (!props.nextDayBetween) return false;

			const [start, end] = props.nextDayBetween;
			if (start == null || end == null) return;

			return start < value && value < end;
		}

		function formatTime(value: number) {
			const hour = Math.floor(value / 60);
			const minute = value % 60;

			const formattedTime = DateTime.now()
				.set({ hour, minute })
				.toLocaleString(DateTime.TIME_SIMPLE, { locale: user.value?.preferredLang });

			return formattedTime;
		}

		const startRange = computed(() => props.availableRange?.at(0) || 0);
		const endRange = computed(() => props.availableRange?.at(1) || 1440);

		const isBetweenRange = (value: number) => {
			return startRange.value <= value && endRange.value >= value;
		};

		const items = computed(() => {
			const items: TimeItem[] = [];
			for (let i = 0; i <= max / step; i++) {
				const time = i * step;

				if (isBetweenRange(time)) {
					items.push({
						value: time,
						text: formatTime(time),
					});
				}
			}

			return items;
		});

		return () => (
			<FormField label={label.value}>
				<Select v-model={vModel.value} class="w-full" onBlur={() => emit("blur")}>
					{{
						append: () =>
							vModel.value != null &&
							isNextDay(vModel.value) && (
								<div class="text-base-content/60">{t("alert-settings.nextDay")}</div>
							),

						default: () => (
							<For each={items.value}>
								{(item) => (
									<SelectOption key={item.value} value={item.value} disabled={item.disabled}>
										{item.text}
									</SelectOption>
								)}
							</For>
						),
					}}
				</Select>
			</FormField>
		);
	},
});
